import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { inject } from '@angular/core';
import { IndicatorStore } from '../indicator/indicator.state';
import { DataLayerStore } from '../data-layer/data-layer.state';
import { IndicatorDataStore } from '../indicatpr-data/indicator-data.state';
import { WidgetHelperService } from '../../services/widget/widget-helper.service';
import { DataGroupProperty } from '../indicatpr-data/indicator-data.widget-selectors';
import { EntityId } from '@ngrx/signals/entities';
import { withWidgetCoreFeatures } from './widget.core-features';
import { makeUniqueStringArray } from '../../utility/array.utility';
import { ChartDataset } from 'chart.js';
import {
  CustomGridStackWidget,
  RegressionData,
  ScatterData,
  WegweiserData,
  WegweiserIndicator,
  WegweiserRegion,
  WidgetConfig,
  WidgetType,
} from '../../services/grid-widget-config.service';
import { ComparisonValueStore } from '../comparison-value/comparison-value.state';
import { ComparisonValue } from '../../models/geo-data';
import { updateAlpha } from '../../utility/color.utility';
import { DataLayer } from '../../models/data-layer';
import { GridStore } from './grid.state';

// ==================== UTILITY FUNCTIONS ====================

/**
 * Computes the minimum value from an array of datasets.
 */
function getMinValue(datasets: { label: string; data: number[] }[]): number {
  return Math.min(...datasets.flatMap(dataset => dataset.data));
}

/**
 * Computes the maximum value from an array of datasets.
 */
function getMaxValue(datasets: { label: string; data: number[] }[]): number {
  return Math.max(...datasets.flatMap(dataset => dataset.data));
}

/**
 * Sorts Chart.js datasets based on given criteria.
 */
function sortChartDatasets(
  datasets: { label: string; data: number[] }[],
  sortBy: 'label' | 'value',
  direction: 'asc' | 'desc'
): { label: string; data: number[] }[] {
  return datasets.sort((a, b) => {
    let compareA: string | number;
    let compareB: string | number;

    if (sortBy === 'label') {
      compareA = a.label.toLowerCase();
      compareB = b.label.toLowerCase();
    } else if (sortBy === 'value') {
      // Assuming sort by the first value in the data array
      compareA = a.data.reduce((sum, val) => sum + val, 0);
      compareB = b.data.reduce((sum, val) => sum + val, 0);
    } else {
      throw new Error('Invalid sortBy criteria. Use "label" or "value".');
    }

    if (direction === 'asc') {
      return compareA < compareB ? -1 : compareA > compareB ? 1 : 0;
    } else {
      return compareA > compareB ? -1 : compareA < compareB ? 1 : 0;
    }
  });
}

/**
 * Sorts chart group data including labels, keys, and datasets. Allows optional sorting
 * functionality based on specified criteria and direction.
 *
 * @param {string[]} labels - An array of chart labels corresponding to data points.
 * @param {string[]} keys - An array of unique keys corresponding to the chart labels.
 * @param {{ label: string; data: number[] }[]} datasets - An array of dataset objects, each containing a label and array of numeric data values.
 * @param {boolean} enableSorting - A boolean value that determines whether sorting should be performed.
 * @param {'label'|'value'} [sortBy] - The criteria to sort by, either 'label' for sorting by label names, or 'value' for sorting by aggregate data values.
 * @param {'asc'|'desc'} [direction] - The sorting direction, either 'asc' for ascending or 'desc' for descending order.
 * @return {{ labels: string[]; datasets: { label: string; data: number[] }[]; groups: any[] }} An object including the sorted labels array, updated datasets, and the computed groups data.
 */
function sortChartGroups(
  labels: string[],
  keys: string[],
  datasets: { label: string; dataKeys: string[]; data: number[] }[],
  enableSorting: boolean,
  sortBy?: 'label' | 'value',
  direction?: 'asc' | 'desc'
): {
  labels: string[];
  datasets: { label: string; data: number[] }[];
  groups: any[];
} {
  // Create an array of objects representing groups
  const groups = labels.map((label, index) => ({
    label: String(label),
    key: String(keys[index]),
    values: datasets.map(dataset => {
      const key = String(keys[index]);
      const dataIndex = dataset.dataKeys?.indexOf(key) || 0;
      return dataset.data[dataIndex];
    }),
  }));

  if (!sortBy || !direction || !enableSorting) {
    return { labels, datasets, groups };
  }

  groups.sort((a, b) => {
    let compareA: string | number;
    let compareB: string | number;

    if (sortBy === 'label') {
      compareA = a.label.toLowerCase();
      compareB = b.label.toLowerCase();
    } else if (sortBy === 'value') {
      compareA = a.values.reduce((sum, val) => sum + Number(val), 0);
      compareB = b.values.reduce((sum, val) => sum + Number(val), 0);
    } else {
      throw new Error('Invalid sortBy criteria. Use "label" or "value".');
    }

    if (direction === 'asc') {
      return compareA < compareB ? -1 : compareA > compareB ? 1 : 0;
    } else {
      return compareA > compareB ? -1 : compareA < compareB ? 1 : 0;
    }
  });

  // Extract sorted labels and datasets
  const sortedLabels = groups.map(group => group.label);
  const sortedDatasets = datasets.map((dataset, datasetIndex) => ({
    ...dataset,
    data: groups.map(group => group.values[datasetIndex]),
    dataKeys: groups.map(group => group.key),
  }));

  return { labels: sortedLabels, datasets: sortedDatasets, groups };
}

/**
 * Normalizes a value to a given range
 */
function normalize(
  value: number,
  min: number,
  max: number,
  scaledMin: number,
  scaledMax: number
): number {
  return ((value - min) / (max - min)) * (scaledMax - scaledMin) + scaledMin;
}

export function withChartMethods() {
  return signalStoreFeature(
    {
      state: type<WidgetState>(),
    },
    withWidgetCoreFeatures(),
    withMethods(
      (
        { activeWidget, widgetType, selected, update, entityMap, get },
        indicatorStore = inject(IndicatorStore),
        layerStore = inject(DataLayerStore),
        indicatorDataStore = inject(IndicatorDataStore),
        gridStore = inject(GridStore),
        comparisonStore = inject(ComparisonValueStore),
        helper = inject(WidgetHelperService)
      ) => {
        /**
         * Processes and retrieves an array of labels or numerical values based on the provided group property in the widget configuration.
         *
         * @function getLabelsByGroupProperty
         * @param {WidgetConfig} config - The configuration object for the widget containing properties to determine how labels are grouped and retrieved.
         * @param {string[]} dataLayerIds - An array of data layer IDs to be processed when the group property is 'dataLayer'.
         * @param {string[]} dataKeys - An array of data keys used to retrieve features when the group property is 'feature'.
         * @returns {string[] | number[]} Returns an array of labels (strings) or years (numbers), depending on the group property specified in the configuration.
         *
         * The function behaves differently based on the `groupProperty` field in the `config` object:
         * - 'indicator' - Returns either the full indicator name or its default name, depending on the `showFullIndicatorName` property.
         * - 'feature' - Returns feature names based on provided `dataKeys`.
         * - 'dataLayer' - Returns an array of numeric values (years) derived from the `dataLayerIds`, excluding any disabled years in the `config`.
         * If the `config` object is missing or invalid, the function returns an empty array.
         */
        const getLabelsByGroupProperty = (
          config: WidgetConfig,
          dataLayerIds: string[],
          dataKeys: string[]
        ): string[] | number[] => {
          if (!config) return [];

          switch (config.groupProperty) {
            case 'indicator':
              return indicatorStore
                .getByIds(config.indicators)
                .map(indicator =>
                  config.showFullIndicatorName
                    ? indicatorStore.getFullName(indicator)
                    : indicator.name
                );
            case 'feature':
              // eslint-disable-next-line no-case-declarations
              const features: any = {
                ...config.features,
                ...comparisonStore.byKey(),
              };
              return dataKeys.map(key => features[key].name);
            default: // dataLayer
              return layerStore
                .getByIds(dataLayerIds)
                .map((layer: DataLayer) => Number(layer.year))
                .filter(year => !config.disabledYears.includes(year))
                .sort((a, b) => b - a);
          }
        };

        /**
         * Retrieves an array of keys based on the group property defined in the configuration.
         *
         * This function processes a configuration object and determines the relevant keys
         * based on the specified groupProperty. It supports three types of grouping:
         * 'indicator', 'feature', and the default 'dataLayer'.
         *
         * @param {WidgetConfig} config - The widget configuration object which includes the
         *                                grouping property and additional sub-properties
         *                                necessary for fetching the keys.
         * @param {string[]} dataLayerIds - Array of data layer identifiers used when the
         *                                  groupProperty is 'dataLayer'.
         * @param {string[]} dataKeys - Array of data keys used when the groupProperty is 'feature'.
         *
         * @returns {string[]} An array of keys that correspond to the specified group property.
         */
        const getKeysByGroupProperty = (
          config: WidgetConfig,
          dataLayerIds: string[],
          dataKeys: string[]
        ) => {
          if (!config) return [];
          switch (config.groupProperty) {
            case 'indicator':
              return indicatorStore
                .getByIds(config.indicators)
                .map(indicator => indicator._id);
            case 'feature':
              // eslint-disable-next-line no-case-declarations
              const features: any = {
                ...config.features,
                ...comparisonStore.byKey(),
              };
              return dataKeys.map(key => features[key].key);
            default: // dataLayer
              return layerStore
                .getByIds(dataLayerIds)
                .filter(
                  layer => !config.disabledYears.includes(Number(layer.year))
                )
                .sort((a, b) => b.year - a.year)
                .map(d => d._id);
          }
        };

        /**
         * Generates a dataset label based on the provided widget configuration, property type, key, and other optional parameters.
         *
         * @param {WidgetConfig} widget - The widget configuration object. Determines the context for generating the label.
         * @param {string} property - The type of property to generate the label for. Supported values are 'indicator', 'feature', or other fallback types.
         * @param {string} key - The unique key or identifier used to retrieve the label value from the respective store.
         * @param {boolean} [indicatorPrefix=false] - Optional indicator prefix flag. If true and the property is 'indicator', the label includes its parent indicator name as a prefix.
         * @param {Record<string, ComparisonValue>} comparisonValues - A record of comparison values used to derive the label for 'feature' type properties when applicable.
         * @returns {string} The generated dataset label based on the provided parameters. Returns an empty string if no valid label is determined.
         */
        const getDatasetLabel = (
          widget: WidgetConfig,
          property: string,
          key: string,
          indicatorPrefix = false,
          comparisonValues: Record<string, ComparisonValue>
        ) => {
          if (!widget) return '';

          switch (property) {
            case 'indicator':
              if (indicatorPrefix) {
                let prefix = '';
                const indicator = indicatorStore.getById(key);
                if (!indicator) return '';
                if (indicator.parent) {
                  prefix = indicatorStore.getById(indicator.parent).name;
                }
                return prefix ? `${prefix}: ${indicator.name}` : indicator.name;
              } else {
                return indicatorStore.getById(key)?.name || '';
              }
            case 'feature':
              return (
                widget.features?.[key]?.name ||
                comparisonValues[key]?.name ||
                ''
              );
            default:
              return String(layerStore.getById(key)?.year || '');
          }
        };

        /**
         * Retrieves the group name associated with a specific widget property and key.
         *
         * This function evaluates the provided property and fetches the corresponding group
         * name from the relevant store, based on the key. If the widget object is invalid or the
         * property is unsupported, the function returns `null`.
         *
         * @param {WidgetConfig} widget - The widget configuration object.
         * @param {string} property - The property of the widget to evaluate.
         *                           Supported value: 'indicator'.
         * @param {string} key - The unique key used to fetch the relevant group name.
         * @returns {string | null} - The name of the group associated with the key if available,
         *                           otherwise `null`.
         */
        const getGroup = (
          widget: WidgetConfig,
          property: string,
          key: string
        ) => {
          if (!widget) return null;

          switch (property) {
            case 'indicator':
              return indicatorStore.getParent(key)?.name || null;
            default:
              return null;
          }
        };

        /**
         * Retrieves legend groups based on a widget's configuration.
         *
         * This function processes the `widget` parameter to determine grouping information
         * for indicators, organizing them into a record structure where the keys represent
         * parent group names and the values are arrays of indicators' names belonging to those groups.
         * If the widget does not meet the required conditions or has no valid group properties,
         * the function returns `null`.
         *
         * @param {WidgetConfig} widget - The widget configuration object containing details about indicators and grouping properties.
         * @returns {Record<string, string[]> | null} An object mapping parent group names to arrays of indicator names,
         *                                           or `null` if no grouping information is found.
         */
        const getLegendGroups = (
          widget: WidgetConfig
        ): Record<string, string[]> | null => {
          if (!widget) return null;

          if (widget.groupProperty === 'indicator') {
            const sets: Record<string, string[]> = {};
            widget.indicators.forEach((id: string) => {
              const parentName = indicatorStore.getParent(id)?.name;
              if (!parentName) return;
              if (!sets[parentName]) {
                sets[parentName] = [];
              }
              sets[parentName].push(indicatorStore.getById(id).name);
            });
            return sets;
          }
          return null;
        };

        /**
         * Retrieves the first suitable property from a predefined set of possible properties
         * that is neither the focusProperty nor the groupProperty of the provided configuration.
         *
         * @function
         * @param {WidgetConfig} conf - The configuration object containing properties used to determine
         *                              the appropriate selection.
         * @returns {DataGroupProperty} - The first property from the predefined set ('feature', 'indicator',
         *                                'dataLayer') that is not included in conf.focusProperty or conf.groupProperty.
         */
        const dataSetProperty = (conf: WidgetConfig) => {
          const possibleProps: DataGroupProperty[] = [
            'feature',
            'indicator',
            'dataLayer',
          ];
          return possibleProps.find(
            prop => ![conf.focusProperty, conf.groupProperty].includes(prop)
          )!;
        };

        /**
         * Calculates and generates an array of scatter plot data from the given inputs.
         *
         * This function takes in arrays of x-coordinates, y-coordinates, and optional radii (rs),
         * and returns a collection of scatter data objects. If the radii array is provided,
         * it normalizes the radius values to fit within a specified range (default of 1 to 20).
         *
         * Each scatter data object includes:
         * - The x-coordinate (mandatory)
         * - The y-coordinate (mandatory)
         * - The scaled radius (`r`) normalized to the range if `rs` is provided, or set to a default of 1
         * - The raw, unscaled radius (`rRaw`) if provided in the input, or undefined otherwise
         *
         * Any data where the x or y value is undefined will be filtered out from the resultant array.
         *
         * @param {number[]} xs - The array of x-coordinates for scatter plot data.
         * @param {number[]} ys - The array of y-coordinates for scatter plot data.
         * @param {number[] | null} rs - An optional array of radii corresponding to each (x, y) pair. Can be null.
         * @returns {ScatterData[]} An array of scatter data objects containing x, y, scaled radius (`r`), and raw radius (`rRaw`) values.
         */
        const calculateScatterData = (
          xs: number[],
          ys: number[],
          rs: number[] | null
        ): ScatterData[] => {
          let minR: number | undefined;
          let maxR: number | undefined;

          if (rs) {
            // Calculate min and max of the `r` values
            minR = Math.min(...rs);
            maxR = Math.max(...rs);
          }

          return xs
            .map((x: number, i: number) => {
              const rawR = rs && rs[i] ? rs[i] : 1;
              const scaledR =
                rs && maxR !== undefined && minR !== undefined
                  ? normalize(rawR, minR, maxR, 1, 20)
                  : 1;

              return {
                x,
                y: ys[i],
                r: scaledR,
                rRaw: rs?.[i] || undefined,
              };
            })
            .filter(
              (data: ScatterData) =>
                data.x !== undefined && data.y !== undefined
            );
        };

        /**
         * Converts grouped data into axis data for a chart, such as scatter or bubble chart data.
         *
         * @param {Record<string, Record<string, number | null>>} groupedData - The grouped data input, structured in a nested key-value format, where the outer keys represent data fields, and the inner keys represent individual data points.
         * @param {WidgetConfig} config - Configuration object for the widget, containing axis properties, chart settings, colorset, and other display customization options.
         * @param {WidgetType} type - The type of widget or chart being generated. Can include types such as 'adder', 'scatter', or 'bubble'.
         * @param {Record<string, ComparisonValue>} comparisonValues - Mapping of comparison values used for computations or supporting additional data labels.
         *
         * @returns {ChartDataset[] | undefined} Returns an array of chart datasets formatted for chart rendering. If regression is enabled in the configuration, regression data is included. Returns `undefined` when inputs are invalid or insufficient for processing.
         */
        const convertToAxisData = (
          groupedData: Record<string, Record<string, number | null>>,
          config: WidgetConfig,
          type: WidgetType,
          comparisonValues: Record<string, ComparisonValue>
        ) => {
          type = type || 'adder';
          const ids = config?.axisProperties.length
            ? config.axisProperties
            : [...Object.keys(groupedData).slice(0, 2)];

          if (type === 'bubble') {
            const radiusValue = Object.keys(groupedData)[2];
            if (radiusValue) ids.push(radiusValue);
          }

          if (!config || !groupedData || !type) {
            return;
          }

          if (
            !ids ||
            (ids.length < 2 && type === 'scatter') ||
            (ids.length < 3 && type === 'bubble')
          ) {
            return;
          }

          const radiusProperty =
            config?.radiusProperty || Object.keys(groupedData)[2] || null;
          if (radiusProperty) {
            ids.push(radiusProperty);
          }

          let label = 'x: ';
          const chartData: ChartDataset = {
            data: [],
            backgroundColor: helper.getChartBackgroundColor(
              type,
              config.colorset,
              config.invertColors,
              0
            ),
            borderColor: helper.getChartBackgroundColor(
              type,
              config.colorset,
              config.invertColors,
              0
            ),
            label: '',
            pointRadius: config.dotSize || 4,
          };

          const dataItems: any = ids.map((id, idx) => {
            const dataLabel = getDatasetLabel(
              config,
              dataSetProperty(config),
              id,
              true,
              comparisonValues
            );
            if (idx === 1) {
              label += ' | y: ';
              config.xAxisLabel = config.axis?.x.label || dataLabel;
            } else if (idx === 0) {
              config.yAxisLabel = config.axis?.y.label || dataLabel;
            }

            label += dataLabel;

            return groupedData[id];
          });

          if (
            !dataItems ||
            !dataItems[0] ||
            !dataItems[1] ||
            dataItems.length < 2
          ) {
            return;
          }

          const xs: number[] = Object.values(dataItems[0]);
          const ys: number[] = Object.values(dataItems[1]);
          const rs: number[] | null = dataItems[2]
            ? Object.values(dataItems[2])
            : null;

          const converted: ScatterData[] = calculateScatterData(
            xs,
            ys,
            rs
          ).filter(data => data.x !== undefined && data.y !== undefined);

          chartData.data = converted;
          chartData.label = label;

          if (config.displayRegression) {
            const regressionData: RegressionData =
              helper.calcRegression(converted);
            const regressionDataset = {
              regression: true,
              label: 'r2: ' + regressionData.r2,
              customTooltip: 'r2: ' + regressionData.r2,
              r2: regressionData.r2,
              data: regressionData.line,
              type: 'line',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 2,
              fill: false,
            };

            return [chartData, regressionDataset];
          } else {
            return [chartData];
          }
        };

        /**
         * Generates grouped Wegweiser data based on the provided widget configuration.
         *
         * This function takes a `widgetId` and determines the configuration for creating
         * data groups. The grouping is dynamically performed based on the `focusProperty`
         * and `groupProperty` defined in the widget configuration. It supports different
         * groupings such as by indicators, regions, and years, or combinations thereof.
         *
         * If no valid configuration is found or if necessary properties are missing,
         * it returns an empty object with default properties for labels, datasets, and groups.
         *
         * @param {string} widgetId - The identifier of the widget whose configuration is used
         *                            to determine grouping criteria.
         * @returns {Object} The grouped Wegweiser data, containing labels, datasets, and groups.
         */
        const getGroupedWegweiserData = (widgetId: string) => {
          const config = widgetId ? get(widgetId) : { ...selected() };
          if (
            !config ||
            !config.type ||
            !config.widget ||
            !config.widget.wegweiserData ||
            !config.widget.wegweiserData?.indicators.length
          ) {
            return { labels: [], datasets: [], groups: [] };
          }

          const focusProperty = config.widget.focusProperty;
          const groupProperty = config.widget.groupProperty;

          switch (focusProperty) {
            case 'dataLayer':
              return groupProperty === 'indicator'
                ? getWegweiserDataLayerDataGroupedByIndicators(
                    Number(config.widget.year),
                    config.type,
                    config.widget
                  )
                : getWegweiserDataLayerDataGroupedByRegions(
                    Number(config.widget.year),
                    config.type,
                    config.widget
                  );
            case 'indicator':
              return groupProperty === 'feature'
                ? getWegweiserIndicatorDataGroupedByRegions(
                    Number(config.widget.indicator),
                    config.type,
                    config.widget
                  )
                : getWegweiserIndicatorDataGroupedByYears(
                    Number(config.widget.indicator),
                    config.type,
                    config.widget
                  );
            case 'feature':
              return groupProperty === 'indicator'
                ? getWegweiserRegionDataGroupedByIndicators(
                    Number(config.widget.feature),
                    config.type,
                    config.widget
                  )
                : getWegweiserRegionDataGroupedByYears(
                    Number(config.widget.feature),
                    config.type,
                    config.widget
                  );
          }
        };

        /**
         * Transforms WegweiserData into a Chart.js data format for a bar chart,
         * where indicators are on the x-axis, and regions are in the legend for a single year.
         *
         * @param year - The ID of the indicator to focus on.
         * @param type - The WidgetType
         * @param widget - The WidgetConfig
         * @returns A Chart.js compatible data object.
         */
        const getWegweiserDataLayerDataGroupedByIndicators = (
          year: number,
          type: WidgetType,
          widget: WidgetConfig
        ) => {
          const data = widget.wegweiserData;
          if (!data) return null;

          let yearIndex = data.years.indexOf(year);
          if (yearIndex === -1) {
            yearIndex = data.years.length - 1;
          }

          const groups: any[] = [];
          // Extract indicator names (x-axis labels)
          const labels = data.indicators.map(indicator => {
            groups.push({
              label: indicator.name,
              key: String(indicator.id),
            });
            return indicator.name;
          });

          // Create datasets for each region
          const datasets = data.regions.map(
            (region: WegweiserRegion, idx: number) => {
              const color = helper.getChartBackgroundColor(
                type,
                widget.colorset,
                widget.invertColors,
                idx
              );

              return {
                label: region.name,
                data: data.indicators.map(indicator => {
                  // Extract the value for the region and given year index
                  const regionIndex = data.regions.findIndex(
                    r => r.id === region.id
                  );
                  return (
                    indicator.regionYearValues[regionIndex]?.[yearIndex] ?? null
                  );
                }),
                dataKeys: data.indicators.map(indicator =>
                  String(indicator.id)
                ),
                backgroundColor: color,
                borderColor: color,
                pointRadius: widget.dotSize || 4,
                borderWidth: widget.borderWidth || 2,
                tension: widget.lineTension || 0,
              };
            }
          );

          return { labels, datasets, groups };
        };

        /**
         * Transforms WegweiserData into a Chart.js data format for a bar chart,
         * where regions are on the x-axis, and indicators are in the legend for a single year.
         *
         * @param year - The ID of the indicator to focus on.
         * @param type - The WidgetType
         * @param widget - The WidgetConfig
         *
         * @returns A Chart.js compatible data object.
         */
        const getWegweiserDataLayerDataGroupedByRegions = (
          year: number,
          type: WidgetType,
          widget: WidgetConfig
        ) => {
          // Find the index of the given year
          const data = widget.wegweiserData;
          if (!data) return null;

          let yearIndex = data.years.indexOf(year);
          if (yearIndex === -1) {
            yearIndex = data.years.length - 1;
          }

          // X-Axis Labels (Regions)
          const groups: any[] = [];
          const labels = data.regions.map(region => {
            groups.push({
              label: region.name,
              key: String(region.id),
            });
            return region.name;
          });

          // Create datasets for each indicator
          const datasets = data.indicators.map(
            (indicator: WegweiserIndicator, idx: number) => {
              const color = helper.getChartBackgroundColor(
                type,
                widget.colorset,
                widget.invertColors,
                idx
              );

              return {
                label: indicator.name,
                data: data.regions.map(region => {
                  // Extract the value for this region and the given year index
                  const regionIndex = data.regions.findIndex(
                    r => r.id === region.id
                  );
                  return (
                    indicator.regionYearValues[regionIndex]?.[yearIndex] ?? null
                  );
                }),
                dataKeys: data.regions.map(region => String(region.id)),
                labelKey: String(indicator.id),
                backgroundColor: color,
                borderColor: color,
                pointRadius: widget.dotSize || 4,
                borderWidth: widget.borderWidth || 2,
                tension: widget.lineTension || 0,
              };
            }
          );

          return { labels, datasets, groups };
        };

        /**
         * Transforms WegweiserData into a Chart.js data format for a bar chart,
         * where years  are on the x-axis, and indicators are in the legend for a single region.
         *
         * @param regionId - The ID of the indicator to focus on.
         * @param type - The WidgetType
         * @param widget - The WidgetConfig
         *
         * @returns A Chart.js compatible data object.
         */
        const getWegweiserRegionDataGroupedByYears = (
          regionId: number,
          type: WidgetType,
          widget: WidgetConfig
        ) => {
          const data = widget.wegweiserData;
          if (!data) return null;

          // Find the index of the selected region
          const regionIndex = data.regions.findIndex(
            region => region.id === regionId
          );
          if (regionIndex === -1) {
            throw new Error(`Region with ID ${regionId} not found.`);
          }

          const groups: any[] = [];
          const disabledYears = widget.disabledYears || [];
          const disabledYearsIdx: number[] = disabledYears.map(disabledYear =>
            data.years.indexOf(disabledYear)
          );
          const availableYears = data.years.filter(
            year => !disabledYears.includes(year)
          );

          // X-Axis Labels (Years)
          const labels = availableYears.map(year => {
            groups.push({
              label: String(year),
              key: year,
            });
            return String(year);
          });

          // Create datasets for each indicator
          const datasets = data.indicators.map(
            (indicator: WegweiserIndicator, idx: number) => {
              const color = helper.getChartBackgroundColor(
                type,
                widget.colorset,
                widget.invertColors,
                idx
              );

              return {
                label: indicator.name,
                labelKey: String(indicator.id),
                data:
                  indicator.regionYearValues[regionIndex]?.filter(
                    (val, idx) => !disabledYearsIdx.includes(idx)
                  ) ?? [],
                dataKeys: labels,
                backgroundColor: color,
                borderColor: color,
                pointRadius: widget.dotSize || 4,
                borderWidth: widget.borderWidth || 2,
                tension: widget.lineTension || 0,
              };
            }
          );

          return { labels, datasets, groups };
        };

        /**
         * Transforms WegweiserData into a Chart.js data format for a bar chart,
         * where indicators are on the x-axis, and years are in the legend for a single region.
         *
         * @param regionId - The ID of the indicator to focus on.
         * @param type - The WidgetType
         * @param widget - The WidgetConfig
         * @returns A Chart.js compatible data object.
         */
        const getWegweiserIndicatorDataGroupedByRegions = (
          indicatorId: number,
          type: WidgetType,
          widget: WidgetConfig
        ) => {
          const data = widget.wegweiserData;
          if (!data) return null;

          const indicatorIndex = data.indicators.findIndex(
            indicator => indicator.id === indicatorId
          );
          if (indicatorIndex === -1) {
            throw new Error(`Indicator with ID ${indicatorId} not found.`);
          }

          const indicator = data.indicators[indicatorIndex];
          const groups: any[] = [];

          // X-Axis Labels (Indicators)
          const labels = data.regions.map(region => {
            groups.push({
              label: region.name,
              key: String(region.id),
            });
            return region.name;
          });

          const datasets = data.years.map((year, idx) => {
            const color = helper.getChartBackgroundColor(
              type,
              widget.colorset,
              widget.invertColors,
              idx
            );

            return {
              label: String(year),
              labelKey: String(year),
              data: indicator.regionYearValues.map(
                regionValues => regionValues[idx] ?? null
              ),
              dataKeys: data.regions.map(region => String(region.id)),
              backgroundColor: color,
              borderColor: color,
              pointRadius: widget.dotSize || 4,
              borderWidth: widget.borderWidth || 2,
              tension: widget.lineTension || 0,
            };
          });

          return { labels, datasets, groups };
        };

        /**
         * Transforms WegweiserData into a Chart.js data format for a bar chart,
         * where years are on the x-axis, and regions are in the legend for a single indicator.
         *
         * @param indicatorId - The ID of the indicator to focus on.
         * @param type - The WidgetType
         * @param widget - The WidgetConfig
         * @returns A Chart.js compatible data object.
         */
        const getWegweiserIndicatorDataGroupedByYears = (
          indicatorId: number,
          type: WidgetType,
          widget: WidgetConfig
        ) => {
          // Find the selected indicator
          const data = widget.wegweiserData;
          if (!data) return null;

          const indicatorIndex = data.indicators.findIndex(
            indicator => indicator.id === indicatorId
          );
          if (indicatorIndex === -1) {
            throw new Error(`Indicator with ID ${indicatorId} not found.`);
          }

          const indicator = data.indicators[indicatorIndex];
          const groups: any[] = [];

          const labels = data.years.map(year => {
            groups.push({
              label: String(year),
              key: year,
            });

            return String(year);
          });

          // Create datasets for each region
          const datasets = data.regions.map((region, idx) => {
            const color = helper.getChartBackgroundColor(
              type,
              widget.colorset,
              widget.invertColors,
              idx
            );

            return {
              label: region.name,
              labelKey: String(region.id),
              data: indicator.regionYearValues[idx] ?? [],
              dataKeys: labels,
              backgroundColor: color,
              borderColor: color,
              pointRadius: widget.dotSize || 4,
              borderWidth: widget.borderWidth || 2,
              tension: widget.lineTension || 0,
            };
          });

          return { labels, datasets, groups };
        };

        /**
         * Transforms WegweiserData into a Chart.js data format for a bar chart,
         * where regions are on the x-axis, and years are in the legend for a single indicator.
         *
         * @param regionId - The ID of the indicator to focus on.
         * @param type - The WidgetType
         * @param widget - The WidgetConfig
         * @returns A Chart.js compatible data object.
         */
        const getWegweiserRegionDataGroupedByIndicators = (
          regionId: number,
          type: WidgetType,
          widget: WidgetConfig
        ) => {
          const data = widget.wegweiserData;
          if (!data) return null;

          // Find the selected indicator
          const regionIndex = data.regions.findIndex(
            region => region.id === regionId
          );
          if (regionIndex === -1) {
            throw new Error(`Region with ID ${regionId} not found.`);
          }

          const groups: any[] = [];
          // X-Axis Labels (Regions)
          const labels = data.indicators.map(indicator => {
            groups.push({
              label: indicator.name,
              key: String(indicator.id),
            });
            return indicator.name;
          });

          const datasets = data.years.map((year, idx) => {
            const color = helper.getChartBackgroundColor(
              type,
              widget.colorset,
              widget.invertColors,
              idx
            );

            return {
              label: String(year),
              labelKey: String(year),
              data: data.indicators.map(
                indicator =>
                  indicator.regionYearValues[regionIndex][idx] ?? null
              ),
              dataKeys: data.indicators.map(indicator => String(indicator.id)),
              backgroundColor: color,
              borderColor: color,
              pointRadius: widget.dotSize || 4,
              borderWidth: widget.borderWidth || 2,
              tension: widget.lineTension || 0,
            };
          });

          return { labels, datasets, groups };
        };

        /**
         * Gets valid keys by filtering out disabled items based on the dataset property.
         * The dsProperty determines what the datasets represent (feature, indicator, or dataLayer).
         *
         * @param config - The widget configuration
         * @param data - The data object to extract keys from
         * @param dsProperty - The property that determines what the datasets represent
         * @param dataLayerIds - Array of data layer IDs (used when dsProperty is 'dataLayer')
         * @returns Filtered array of valid keys
         */
        function getValidKeys(
          config: WidgetConfig,
          data: Record<string, any>,
          dsProperty: DataGroupProperty,
          dataLayerIds: string[]
        ): string[] {
          const keys = Object.keys(data);

          if (!config) return keys;

          switch (dsProperty) {
            case 'feature':
              // Filter out disabled features
              // eslint-disable-next-line no-case-declarations
              const disabledFeatures = config.disabledFeatures || [];
              // eslint-disable-next-line no-case-declarations
              const availableFeatureKeys = config.features
                ? Object.keys(config.features)
                : [];

              return keys.filter(
                key =>
                  !disabledFeatures.includes(key) &&
                  (availableFeatureKeys.length === 0 ||
                    availableFeatureKeys.includes(key))
              );

            case 'dataLayer':
              // Filter out disabled years by checking the dataLayerIds
              // eslint-disable-next-line no-case-declarations
              const disabledYears = config.disabledYears || [];

              return dataLayerIds.filter(layerId => {
                const layer = layerStore.getById(layerId);
                return layer && !disabledYears.includes(Number(layer.year));
              });

            case 'indicator':
              // Indicators don't need filtering - return all keys
              return keys;

            default:
              return keys;
          }
        }

        /**
         * Creates a dataset with styling configurations for use in chart rendering.
         *
         * @param {any} data - The input data for the dataset.
         * @param {WidgetConfig} config - Configuration settings for the dataset and its styling options.
         * @param {WidgetType} type - The widget type (e.g., chart type) that determines certain styling characteristics.
         * @param {string} labelKey - The key used to identify the label for the dataset.
         * @param {number} idx - The current index, often used for color or styling variations in multiple datasets.
         * @param {string[]} dataKeys - An array of keys representing data fields used in the dataset.
         * @param {Record<string, ComparisonValue>} keyValueComparisonValues - A mapping of keys to their comparison values, used for determining labels and tooltips.
         * @param {string[] | null} selectedFeatures - An array of selected feature identifiers, or null if no features are selected.
         * @param {string | undefined} highlightedFeature - The identifier of a highlighted feature, if any.
         * @param {string} dsProperty - A property key that determines specific dataset behavior or grouping.
         *
         * @returns {Object} The formatted dataset object, including data values, visual styling (e.g., colors, borders), labels, tooltips, and group information.
         */
        const createDatasetWithStyling = (
          data: any,
          config: WidgetConfig,
          type: WidgetType,
          labelKey: string,
          idx: number,
          dataKeys: string[],
          dataLayerIds: string[],
          keyValueComparisonValues: Record<string, ComparisonValue>,
          selectedFeatures: Array<{ key: string; type: string }> | null,
          highlightedFeature: { key: string; type: string } | undefined,
          dsProperty: string
        ) => {
          const label = getDatasetLabel(
            config,
            dsProperty,
            labelKey,
            false,
            keyValueComparisonValues
          );
          const tooltip = getDatasetLabel(
            config,
            dsProperty,
            labelKey,
            true,
            keyValueComparisonValues
          );

          const color = helper.getChartBackgroundColor(
            type,
            config.colorset,
            config.invertColors,
            idx,
            config.featureOpacity || 1,
            Object.values(data).length
          );

          // Get valid keys based on what the datasets represent
          const keys = getValidKeys(
            config,
            data,
            dsProperty as DataGroupProperty,
            dataLayerIds
          );

          let borderColor;
          let borderWidth: number | number[] = Array(dataKeys.length).fill(
            config.borderWidth || 2
          );
          let borderDash: number[] | undefined = undefined; // For line charts with selections

          // Handle highlighted feature/indicator/dataLayer
          if (highlightedFeature) {
            borderColor = updateAlpha(color, 1);

            // Determine which dimension to highlight based on the type
            if (highlightedFeature.type === config.groupProperty) {
              // Highlighting affects the x-axis (groups) - modify specific bar/point in each dataset
              const highlightIndex = keys.indexOf(highlightedFeature.key);
              if (highlightIndex !== -1) {
                borderWidth[highlightIndex] = 4;
              }
            } else if (highlightedFeature.type === dsProperty) {
              // Highlighting affects the datasets themselves - make entire dataset bold
              if (labelKey === highlightedFeature.key) {
                borderWidth = Array(dataKeys.length).fill(4);
              }
            }
          }

          // Handle selected features/indicators/dataLayers
          if (selectedFeatures && selectedFeatures.length) {
            // Group selected items by type
            const selectedByType = {
              onGroups: selectedFeatures
                .filter(item => item.type === config.groupProperty)
                .map(item => item.key),
              onDatasets: selectedFeatures
                .filter(item => item.type === dsProperty)
                .map(item => item.key),
            };

            // Apply selection styling based on where the selections are
            if (selectedByType.onGroups.length > 0) {
              // Selections on x-axis (groups) - modify specific bars/points
              const selectedIndices = selectedByType.onGroups
                .map(key => keys.indexOf(key))
                .filter(idx => idx !== -1);

              if (selectedIndices.length > 0) {
                // For line charts, keep the original color but use dashed lines
                if (type === 'line') {
                  borderColor = color; // Keep original color
                  borderDash = [5, 3]; // Dashed line pattern [dash length, gap length]
                  borderWidth = Array(dataKeys.length).fill(
                    (config.borderWidth || 2) + 1
                  ); // Slightly thicker
                } else {
                  borderColor = helper.modifyChartColors(
                    color,
                    selectedIndices,
                    {
                      color: '#000000',
                      opacity: config.featureOpacity
                        ? config.featureOpacity + 0.4
                        : 1,
                    },
                    dataKeys.length
                  );
                }
              }
            }

            if (selectedByType.onDatasets.length > 0) {
              // Selections on legend (datasets) - make entire dataset bold
              if (selectedByType.onDatasets.includes(labelKey)) {
                // For line charts, use dashed line instead of black color
                if (type === 'line') {
                  borderColor = color; // Keep original color
                  borderDash = [5, 3]; // Dashed line pattern
                  borderWidth = Array(dataKeys.length).fill(
                    (config.borderWidth || 2) + 1
                  );
                } else {
                  borderColor = '#444444';
                  // Optionally also make it bold if not already
                  if (!Array.isArray(borderWidth) || borderWidth[0] !== 4) {
                    borderWidth = Array(dataKeys.length).fill(3);
                  }
                }
              }
            }

            // Fallback: If no specific styling applied, use default enhanced opacity
            if (!borderColor) {
              borderColor = updateAlpha(
                color,
                config.featureOpacity ? config.featureOpacity + 0.4 : 1
              );
            }
          } else if (!highlightedFeature) {
            borderColor = updateAlpha(
              color,
              config.featureOpacity ? config.featureOpacity + 0.4 : 1
            );
          }

          return {
            data: Object.values(data),
            dataKeys: dataKeys,
            backgroundColor: color,
            borderColor: borderColor,
            borderDash: borderDash, // Add borderDash property
            label: label,
            customTooltip: tooltip,
            labelKey: labelKey,
            featureType: dataSetProperty(config),
            pointRadius: config.dotSize || 4,
            borderWidth: borderWidth,
            tension: config.lineTension || 0,
            group: getGroup(config, dsProperty, labelKey),
          };
        };

        const addComparisonDatasets = (
          datasets: any[],
          config: WidgetConfig,
          type: WidgetType,
          comparisonData: any,
          featureData: any
        ) => {
          if (
            type !== 'bar' ||
            !config.comparisonValues ||
            !config.comparisonValues.length
          ) {
            return datasets;
          }

          datasets = datasets.map((dataset: any) => {
            dataset.type = 'bar';
            return dataset;
          });

          const compSets: any[] = [];
          config.comparisonValues.forEach((compKey, compIndex) => {
            const data = comparisonData[compKey];
            if (
              data &&
              (config.groupProperty === 'feature' ||
                config.focusProperty === 'feature')
            ) {
              Object.values(data).forEach((item: any, idx) => {
                const labelKey = Object.keys(featureData)[idx];
                const color = helper.getChartBackgroundColor(
                  type,
                  config.colorset,
                  config.invertColors,
                  idx
                );
                compSets.push(
                  helper.getComparisonLineOptions(
                    Object.values(item),
                    Object.keys(item),
                    {
                      color,
                      compIndex,
                      legendIndex: idx,
                      labelKey: labelKey,
                      tooltip: comparisonStore.getNameByKey(compKey),
                      asBar: !datasets.length,
                    }
                  )
                );
              });
            } else if (data) {
              compSets.push(
                helper.getComparisonLineOptions(
                  Object.values(data),
                  Object.keys(data),
                  {
                    color: 'red',
                    tooltip: comparisonStore.getNameByKey(compKey),
                    asBar: !datasets.length,
                  }
                )
              );
            }
          });

          datasets = datasets.concat(compSets);
          return datasets;
        };

        // ==================== MAIN UPDATE METHOD ====================

        const updateChartData = (id?: EntityId, rerender = true) => {
          const selectedFeatures =
            gridStore.options()?.selectedFeatures || null;
          const highlightedFeature = gridStore.options()?.highlightedFeature;
          let widgetById: CustomGridStackWidget | null = null;
          const keyValueComparisonValues = comparisonStore.byKey();

          if (id) {
            widgetById = get(id);
          }

          const config = widgetById ? widgetById.widget : activeWidget();
          const type = widgetById ? widgetById.type : widgetType();
          let layerIds: string[] = [];
          let dataKeys: string[] = [];

          if (!config || !type) return null;

          // force sort overwrite when group property is dataLayer (years)
          if (config.groupProperty === 'dataLayer') {
            config.sort = {
              enable: true,
              property: 'label',
              direction: 'asc',
            };
          }
          // Handle external/wegweiser data
          if (config.context !== 'local') {
            const data = getGroupedWegweiserData(id as string);

            if (data && data.datasets) {
              config.chartData = {
                datasets: data.datasets,
                labels: data.labels,
                minValue: getMinValue(data.datasets),
                maxValue: getMaxValue(data.datasets),
              };

              const current = widgetById || { ...selected() };
              current.widget = config;
              update(current.id as string, current, rerender);
            }
            return null;
          }

          // Handle local data
          const dsProperty = dataSetProperty(config);
          const groupedData = indicatorDataStore.groupByProperties(
            config[config.focusProperty],
            config.focusProperty,
            config.groupProperty,
            dsProperty,
            {
              indicators: config.indicators,
              disabledYears: config.disabledYears,
              dataLayerType: config.dataLayerType,
              disabledDataLayers: layerStore.getIdsByYears(
                config.dataLayerType,
                config.disabledYears
              ),
              disabledFeatures: config.disabledFeatures,
              availableFeatureKeys: Object.keys(config.features || []),
              comparisonKeys: Object.keys(keyValueComparisonValues),
            }
          );

          const comparisonData = groupedData.comparison;
          const featureData = groupedData.features;
          const dataLayerIds = groupedData.dataLayerIds;

          let datasets: any;

          if (helper.isAxisBasedType(type || 'adder')) {
            datasets = convertToAxisData(
              featureData,
              config,
              type,
              keyValueComparisonValues
            );
          } else if (config && type) {
            datasets = Object.values(featureData).map((item, idx) => {
              const labelKey = Object.keys(featureData)[idx];
              const setKeys = Object.keys(item);
              dataKeys = setKeys;

              layerIds = layerIds.concat(
                config.groupProperty === 'dataLayer'
                  ? Object.keys(item)
                  : Object.keys(featureData)
              );

              return createDatasetWithStyling(
                item,
                config,
                type,
                labelKey,
                idx,
                dataKeys,
                dataLayerIds,
                keyValueComparisonValues,
                selectedFeatures,
                highlightedFeature,
                dsProperty
              );
            });

            datasets = addComparisonDatasets(
              datasets,
              config,
              type,
              comparisonData,
              featureData
            );
          }

          if (datasets) {
            const labels = getLabelsByGroupProperty(
              config,
              dataLayerIds,
              dataKeys
            );
            const keys = getKeysByGroupProperty(config, dataLayerIds, dataKeys);

            config.chartData = sortChartGroups(
              labels as string[],
              keys as string[],
              datasets,
              config.sort.enable,
              config.sort.property,
              config.sort.direction
            );

            config.chartData.legendGroups = getLegendGroups(config);
            config.chartData.minValue = getMinValue(datasets);
            config.chartData.maxValue = getMaxValue(datasets);
            config.groupedData = groupedData;
            config.dataLayers = makeUniqueStringArray(layerIds);
            config.dataSetProperty = dataSetProperty(config);

            const current = widgetById || { ...selected() };
            current.widget = config;
            update(current.id as string, current, rerender);
          }

          return null;
        };

        const getChartData = (id: EntityId) => {
          const widget = entityMap()[id];
          if (!widget || !widget.widget) return null;
          return widget.widget.chartData;
        };

        // ==================== RETURN METHODS ====================

        return {
          getLabelsByGroupProperty,
          getKeysByGroupProperty,
          getDatasetLabel,
          getGroup,
          getLegendGroups,
          dataSetProperty,
          updateChartData,
          calculateScatterData,
          convertToAxisData,
          getChartData,
          getGroupedWegweiserData,
          getWegweiserDataLayerDataGroupedByIndicators,
          getWegweiserDataLayerDataGroupedByRegions,
          getWegweiserIndicatorDataGroupedByRegions,
          getWegweiserIndicatorDataGroupedByYears,
          getWegweiserRegionDataGroupedByYears,
          getWegweiserRegionDataGroupedByIndicators,
        };
      }
    )
  );
}
